import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/marketing/Wordmark";

const LINKS = [
  { to: "/pricing", label: "Sleep Help", id: "sleep-help" },
  { to: "/courses", label: "Sleep Courses", id: "courses" },
  { to: "/resources", label: "Free Sleep Guides", id: "resources" },
  { to: "/register?next=/portal/progress", label: "Free Sleep Tracker", id: "tracker" },
  { to: "/#instructor", label: "About Andrea", id: "instructor", hash: true },
];

function NavLink({ l, className, onClick, testid }) {
  if (l.hash) return <a href={l.to} onClick={onClick} data-testid={testid} className={className}>{l.label}</a>;
  return <Link to={l.to} onClick={onClick} data-testid={testid} className={className}>{l.label}</Link>;
}

export default function SiteNav() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const portalHref = user && user.role === "admin" ? "/admin" : user && user.role === "operator" ? "/operator" : user ? "/portal" : "/login";

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 glass-header bg-[#172A3A]/90 border-b border-[#BDA677]/25">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="group"><Wordmark /></Link>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <NavLink key={l.to} l={l} testid={`nav-${l.id}`}
                className="text-[#C9CFD6] hover:text-white text-[13.5px] tracking-[0.03em] link-underline transition-colors" />
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <Link to={portalHref} data-testid={user ? "nav-portal" : "nav-signin"}
              className="text-[13px] text-[#C9CFD6] hover:text-white transition-colors">{user ? "Student Portal" : "Sign In"}</Link>
            <button data-testid="nav-cta" onClick={() => nav("/register")}
              className="btn-clay px-5 py-2.5 rounded-none text-[13px] tracking-[0.04em]">Create Free Account</button>
          </div>

          <button className="lg:hidden text-[#F5F2E9] -mr-1 p-1" data-testid="nav-menu-toggle" onClick={() => setOpen(true)} aria-label="Open menu">
            <Menu size={24} />
          </button>
        </div>
      </header>

      {open && (
        <div className="lg:hidden fixed inset-0 z-[70] bg-[#172A3A] flex flex-col overflow-y-auto overscroll-contain fade-up" data-testid="mobile-menu">
          <div className="h-20 px-5 flex items-center justify-between border-b border-[#BDA677]/25 shrink-0">
            <Link to="/" onClick={() => setOpen(false)}><Wordmark /></Link>
            <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu"
              className="w-11 h-11 -mr-2 grid place-items-center text-[#F5F2E9]"><X size={26} /></button>
          </div>
          <nav className="flex-1 flex flex-col px-6 pt-8 gap-1">
            {LINKS.map((l) => (
              <NavLink key={l.to} l={l} onClick={() => setOpen(false)} testid={`mobile-nav-${l.id}`}
                className="font-academic text-[28px] leading-tight text-[#F5F2E9] py-3 border-b border-[#2B4053]" />
            ))}
            <Link to={portalHref} onClick={() => setOpen(false)} data-testid="mobile-nav-portal"
              className="font-academic text-[28px] leading-tight text-[#E4E0D5] py-3">
              {user ? "Student Portal" : "Sign In"}
            </Link>
          </nav>
          <div className="px-6 pb-10 pt-4 shrink-0">
            <button onClick={() => { setOpen(false); nav("/register"); }} data-testid="mobile-nav-cta"
              className="btn-clay w-full py-4 text-base">Create a Free Student Account</button>
          </div>
        </div>
      )}
    </>
  );
}
